import { Check, Copy } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Banner } from '@/components/feedback/Banner'
import { Button } from '@/components/ui/Button'
import { Sheet } from '@/components/ui/Sheet'
import type { CreatedUser } from '@/types/domain'

/* The only place a password is visible in this INTERFACE.
 *
 * The server generates the credential, returns it exactly once, and stores only
 * its Argon2id encoding — so there is no screen anywhere that can show it
 * again, and nothing to build one from. A Super-admin who closes this without
 * copying issues a reset; that is the intended recovery, not a gap.
 *
 * The credential is USUALLY also mailed to its owner, and the dialog says which
 * case this is rather than assuming (QW-5). `credentialMailed` is the server's
 * answer: true when the mail was queued to the owner's own address through a
 * live sender, false for no mailer, the log sender, a failed enqueue or a
 * non-production redirect. Before it existed this dialog said "has been
 * emailed" on installations with no SMTP at all, where the message went to a
 * log line. True means QUEUED, so the mailed branch says "is being emailed"
 * and keeps the reset advice for a mail that never arrives; the other branch
 * says plainly that the admin must hand the password over. Both promise the
 * first-login change, which RequireSession and the server now enforce.
 *
 * The dialog is kept in both cases because it is the fallback the email cannot
 * be: it still works when the address has a typo in it and when the new
 * colleague is standing at the desk.
 *
 * Everything here follows from "once": the value is selectable and copyable
 * rather than merely displayed, closing is an explicit acknowledgement rather
 * than a click-outside, and the copy says plainly that it will not be shown
 * again. A dialog that dismisses itself on a stray click loses the copy that
 * is easiest to act on.
 */
export function GeneratedCredentialDialog({
  result,
  onClose,
}: {
  result: CreatedUser | null
  onClose: () => void
}) {
  if (!result) return null

  /* Keyed on the credential itself, so a second hand-off mounts a fresh dialog
     rather than reusing the first one's "Copied" state. Remounting is how this
     resets — an effect that called setCopied(false) on open would cascade a
     render every time the dialog appeared. */
  return <CredentialPanel key={result.generatedPassword} result={result} onClose={onClose} />
}

function CredentialPanel({
  result,
  onClose,
}: {
  result: CreatedUser
  onClose: () => void
}) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  const copy = () => {
    void navigator.clipboard
      .writeText(result.generatedPassword)
      .then(() => setCopied(true))
      // Clipboard access can be refused outright; the value is on screen and
      // selectable either way, so this must not surface as a failure.
      .catch(() => setCopied(false))
  }

  return (
    <Sheet
      open
      // Deliberately the same as the explicit button: whatever dismisses this,
      // the credential is gone. Nothing here pretends otherwise.
      onClose={onClose}
      title={t('directories.users.credentialTitle', { name: result.user.name })}
      footer={
        <Button className="w-full" onClick={onClose}>
          {t('directories.users.credentialDone')}
        </Button>
      }
    >
      <div className="flex flex-col gap-3.5">
        {/* Not mailed is a job for the reader, so it is a Banner they cannot
            skim past, with an icon and a sentence rather than a colour alone. */}
        {result.credentialMailed ? null : (
          <Banner title={t('directories.users.credentialNotMailed')} />
        )}

        <p className="text-text-body text-[13px] leading-relaxed">
          {result.credentialMailed
            ? t('directories.users.credentialIntroMailed', { email: result.user.email })
            : t('directories.users.credentialIntroNotMailed', { email: result.user.email })}
        </p>

        <div className="border-line bg-surface-sunken flex items-center gap-2 rounded-[var(--radius-DEFAULT)] border p-3">
          {/* select-all so a double-click takes the whole value: a partially
              selected password is handed over broken and reads as "the system
              gave me a password that does not work". */}
          <code className="text-text-heading flex-1 font-mono text-[13.5px] break-all select-all">
            {result.generatedPassword}
          </code>
          <Button
            size="sm"
            variant="secondary"
            iconLeft={copied ? Check : Copy}
            onClick={copy}
            aria-label={t('directories.users.credentialCopy')}
          >
            {copied
              ? t('directories.users.credentialCopied')
              : t('directories.users.credentialCopy')}
          </Button>
        </div>

        <p className="text-text-muted text-[12.5px] leading-relaxed">
          {t('directories.users.credentialOnce')} {t('directories.users.credentialFirstLogin')}
          {/* Reset advice only where a reset would mail: with no live sender a
              second password reaches nobody either. */}
          {result.credentialMailed ? ` ${t('directories.users.credentialMailMissing')}` : null}
        </p>
      </div>
    </Sheet>
  )
}
